import React, { useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Button } from '@rentspree/component-2023.components.atoms.button';

import { EmailIcon } from '../../components/atoms/icons';
import { ScreeningPackageSelector } from '../../components/molecules/ScreeningPackageSelector';
import { SectionHeader } from '../../components/organisms/SectionHeader';
import { MultiStepLayout } from '../../components/templates/MultiStepLayout';
import {
  ScreeningPackage,
  SCREENING_PACKAGES,
} from '../../types/tenantScreening';

export interface PackageSelectionStepProps {
  selectedPackage?: ScreeningPackage | undefined;
  onPackageChange: (packageType: ScreeningPackage) => void;
  onNext: () => void;
  onBack: () => void;
  onHomeClick?: () => void;
  onExitClick?: () => void;
  isLoading?: boolean | undefined;
}

export const PackageSelectionStep: React.FC<PackageSelectionStepProps> = ({
  selectedPackage,
  onPackageChange,
  onNext,
  onBack,
  onHomeClick,
  onExitClick,
  isLoading = false,
}) => {
  const [localSelectedPackage, setLocalSelectedPackage] = useState<
    ScreeningPackage | undefined
  >(selectedPackage);

  const handlePackageSelect = (packageType: ScreeningPackage) => {
    setLocalSelectedPackage(packageType);
    onPackageChange(packageType);
  };

  const handleNext = () => {
    if (localSelectedPackage && !isLoading) {
      onNext();
    }
  };

  return (
    <MultiStepLayout
      currentStep={5}
      total={7}
      currentSubStepPercentage={100}
      onBack={onBack}
      onNext={handleNext}
      onHomeClick={onHomeClick}
      onExitClick={onExitClick}
      backDisabled={isLoading}
      nextDisabled={!localSelectedPackage || isLoading}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 700, md: 900, lg: 1000 },
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <SectionHeader
          title='Choose your screening package'
          description='Select the tenant screening package that best fits your needs. All packages include secure online applications and instant notifications.'
          align='center'
        />
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <ScreeningPackageSelector
            packages={SCREENING_PACKAGES}
            selectedPackage={localSelectedPackage}
            onPackageChange={handlePackageSelect}
            showSampleReportLink={false}
          />
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            p: 3,
            backgroundColor: 'background.default',
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'text.primary',
            }}
          >
            Why choose our screening services?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                ⚡ Instant results
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                🔒 FCRA compliant
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                  Email notifications
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleNext}
            disabled={!localSelectedPackage || isLoading}
            size='large'
            sx={{ minWidth: 200 }}
          >
            {isLoading ? 'Processing...' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </MultiStepLayout>
  );
};

export default PackageSelectionStep;
